import type { Ladder } from "@/lib/tool-kit/games-organizers/word-ladder";

export type HintMode = "none" | "mark" | "fill";

const BOX =
  "flex h-9 w-9 shrink-0 items-center justify-center border border-current font-mono text-lg uppercase sm:h-10 sm:w-10";

function Rung({
  word,
  changed,
  mode,
  answer,
}: {
  word: string | null;
  /** The position that changes at this rung, or -1 for a rung that never changes. */
  changed: number;
  mode: HintMode;
  /** Draw the whole word: the start, the end, and every rung of the key. */
  answer: boolean;
}) {
  const length = word?.length ?? 0;
  return (
    <span className="flex gap-1">
      {Array.from({ length }, (_, i) => {
        const letter = word?.[i] ?? "";
        const isChange = i === changed;
        let text = "";
        if (answer || (mode === "fill" && !isChange)) {
          text = letter;
        }
        let emphasis = "";
        if (isChange && answer) {
          emphasis = "font-bold underline decoration-2 underline-offset-4";
        } else if (isChange && mode !== "none") {
          emphasis = "border-4";
        }
        return (
          <span
            className={`${BOX} ${emphasis}`}
            // biome-ignore lint/suspicious/noArrayIndexKey: letter boxes are positional
            key={i}
          >
            {text}
          </span>
        );
      })}
    </span>
  );
}

/**
 * The ladder as a worksheet: the start word, empty rungs, the end word. With `answers` it
 * is the key — every rung written in, the letter that changes underlined. `mode` adds the
 * scaffolds: a heavy box on the letter that changes, or every other letter filled in.
 */
export default function LadderRows({
  ladder,
  answers,
  mode,
  extraRows,
  label,
}: {
  ladder: Ladder;
  answers: boolean;
  mode: HintMode;
  extraRows: number;
  label: string;
}) {
  const middle = ladder.words.slice(1, -1);
  const length = ladder.words[0]?.length ?? 0;
  const spare = answers ? 0 : extraRows;
  return (
    <ol
      aria-label={label}
      className="mx-auto flex w-fit flex-col gap-1.5 border-current border-x-4 border-double px-4 py-2"
    >
      <li className="flex items-center gap-3">
        <span className="w-6 text-right text-sm" />
        <Rung answer changed={-1} mode="none" word={ladder.words[0] ?? ""} />
      </li>
      {middle.map((word, i) => (
        <li className="flex items-center gap-3" key={`${i + 1}-${word}`}>
          <span className="w-6 text-right text-sm">{i + 1}</span>
          <Rung
            answer={answers}
            changed={ladder.changes[i] ?? -1}
            mode={mode}
            word={word}
          />
        </li>
      ))}
      {Array.from({ length: spare }, (_, i) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: spare rungs are identical blanks
        <li className="flex items-center gap-3" key={`spare-${i}`}>
          <span className="w-6 text-right text-sm">
            {middle.length + i + 1}
          </span>
          <Rung
            answer={false}
            changed={-1}
            mode="none"
            word={"x".repeat(length)}
          />
        </li>
      ))}
      <li className="flex items-center gap-3">
        <span className="w-6 text-right text-sm" />
        <Rung
          answer
          changed={-1}
          mode="none"
          word={ladder.words.at(-1) ?? ""}
        />
      </li>
    </ol>
  );
}
